import { useEffect, useState } from "react";
import { AlertTriangle, CirclePlay, ExternalLink, Info, Sparkles, TriangleAlert, X } from "lucide-react";
import { daylightSummary, formatHours, scoreColor, weatherEmoji, weatherLabel } from "../lib/format";
import { dictionaries } from "../lib/i18n";
import { TAG_COLOR } from "../lib/theme";
import type { DayPlan, Daylight, UiLang, Stop } from "../lib/types";
import { videoSearchUrl } from "../utils/videoLink";
import { AmenityList } from "./AmenityList";
import { CameraStrip } from "./CameraStrip";
import { ClarityBadge } from "./ClarityBadge";
import { ClimatePanel } from "./ClimatePanel";
import { GoldenHourNote } from "./GoldenHourNote";
import { HeritagePanel } from "./HeritagePanel";
import { HikePanel } from "./HikePanel";
import { LinesPanel } from "./LinesPanel";
import { FoldPanel } from "./FoldPanel";
import { LodgingList } from "./LodgingList";
import { ProducerPanel } from "./ProducerPanel";
import { ProtectedAreaPanel } from "./ProtectedAreaPanel";
import { SeaPanel } from "./SeaPanel";
import { SkiPanel } from "./SkiPanel";
import { WinterPanel } from "./WinterPanel";
import { SunlightCard } from "./SunlightCard";

interface Props {
  stop: Stop;
  daylight?: Daylight;
  /**
   * Dagen stoppet ligger på, når planen har en.
   *
   * Trengs for ankomsttiden: den bygges av etappetidene og oppholdene før
   * dette stoppet, og de tallene finnes bare på dagen. Uten den kan panelet
   * si når sola går ned, men ikke om du er framme før den gjør det — og det
   * var nettopp det spørsmålet som manglet et svar.
   */
  day?: DayPlan;
  lang: UiLang;
  /**
   * Tjenestetypene brukeren vil se. Samme filter som på kartet, så et valg
   * gjort der gjelder også når man åpner et stopp — ellers ville panelet
   * motsagt kartet man nettopp filtrerte.
   */
  placeKinds?: Set<string>;
  /**
   * Måneden reisen går i, 1–12.
   *
   * Klimanormalen er tolv tall, og uten måneden svarer de på ingenting. Den
   * kommer fra samme dato som ga stoppet normalen i det hele tatt, så mangler
   * den, er det fordi planen ikke har en dato — og da vises ikke panelet.
   */
  month?: number;
  onClose: () => void;
}

/** Detaljpanel for ett stopp — åpnes fra kartet, profilen eller tidslinja. */
export function StopDrawer({ stop, daylight, day, lang, placeKinds, month, onClose }: Props) {
  const t = dictionaries[lang];
  // Et bilde som ikke lar seg laste skal forsvinne, ikke etterlate en tom ramme.
  const [imageBroken, setImageBroken] = useState(false);

  useEffect(() => setImageBroken(false), [stop.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const mapUrl = `https://www.openstreetmap.org/?mlat=${stop.lat}&mlon=${stop.lon}#map=12/${stop.lat}/${stop.lon}`;

  return (
    <div
      className="drawer-backdrop no-print"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={stop.name}
    >
      <div className="drawer" onClick={(event) => event.stopPropagation()}>
        <button className="drawer-close" onClick={onClose} aria-label={t.close}>
          <X size={20} />
        </button>

        {stop.image && !imageBroken && (
          <figure className="drawer-figure">
            <img
              src={stop.image.src}
              alt={stop.name}
              loading="lazy"
              onError={() => setImageBroken(true)}
            />
            <figcaption>
              {t.photoBy} {stop.image.credit} ·{" "}
              <a href={stop.image.sourceUrl} target="_blank" rel="noreferrer">
                {stop.image.license}
              </a>
            </figcaption>
          </figure>
        )}

        <div className="tag-row" style={{ marginTop: 0 }}>
          {(stop.tags ?? []).map((tag) => (
            <span className="tag" key={tag} style={{ background: TAG_COLOR[tag] }}>
              {t.tagName[tag]}
            </span>
          ))}
        </div>

        <h2>{stop.name}</h2>
        <p className="drawer-blurb">{stop.blurb}</p>

        {/*
          Stedet i bevegelse, rett under bildet og teksten — fordi det svarer
          på det samme spørsmålet de gjør, bare bedre. Ett kuratert bilde er
          valgt fra sin beste kant på sin beste dag; en video viser hvor bratt
          stien er, hvor mange som står der, og hvor lang den veien egentlig
          føles.

          «YouTube-søk» står ved siden av med vilje. Vi har ikke sett videoene,
          og en lenke som ser kuratert ut når den ikke er det, er et løfte vi
          ikke kan holde. Se utils/videoLink.ts for hvorfor det ble et søk og
          ikke en innebygd spiller.
        */}
        <a
          className="stop-video"
          href={videoSearchUrl(stop.name, t.videoCountry)}
          target="_blank"
          rel="noreferrer"
        >
          <CirclePlay size={14} />
          {t.videoLink}
          <span className="stop-video-source">{t.videoSource}</span>
        </a>

        {/*
          Fotturen står over været. Alt annet på et stopp kan leses etterpå,
          men «denne turen tar tolv timer» avgjør om dagen er mulig i det hele
          tatt — og det må stå før man ser på temperaturen.
        */}
        {stop.hike && <HikePanel hike={stop.hike} lang={lang} />}

        {stop.weather && (
          <div className="drawer-weather">
            <span style={{ fontSize: 30 }}>{weatherEmoji(stop.weather.symbol)}</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {weatherLabel(stop.weather.symbol, lang)}
              </div>
              <div className="drawer-weather-detail">
                {stop.weather.tempMin}–{stop.weather.tempMax} °C · {stop.weather.precipitationMm} mm
              </div>
            </div>
            {stop.viewScore && (
              <div style={{ marginLeft: "auto", textAlign: "right" }}>
                <div
                  className="drawer-score"
                  style={{ color: scoreColor(stop.viewScore.score) }}
                >
                  {stop.viewScore.score}
                </div>
                <div className="drawer-score-label">{t.viewIndex}</div>
              </div>
            )}
          </div>
        )}

        {stop.viewScore && (
          <p className="drawer-reason">
            {stop.viewScore.reason}.
            {stop.viewScore.suggestion && ` ${stop.viewScore.suggestion}`}
          </p>
        )}

        {/*
          Kameraet står rett under været, fordi det svarer på det samme
          spørsmålet — bare uten modell. «Lettskyet, utsiktsindeks 74» er et
          anslag; bildet fra fjellovergangen viser om det ligger snø i veibanen.
        */}
        {/*
          Fotolyset står rett under kameraet og været, fordi de tre svarer på
          samme spørsmål fra tre kanter: hva slags lys blir det, hva er lyset nå,
          og når er lyset på sitt beste.
        */}
        {stop.golden && <GoldenHourNote golden={stop.golden} lang={lang} />}

        {/*
          Sol og dagslys på ankomsttidspunktet.

          Står etter fotolyset og ikke før: golden hour er et råd om når man
          bør være her, dette er hva som faktisk møter deg når du kommer. Det
          første er en mulighet, det andre en beskrankning, og beskrankningen
          leses best etter at man vet hva man går glipp av.
        */}
        {day && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.sunTitle}</h3>
            <SunlightCard stop={stop} day={day} lang={lang} />
          </div>
        )}

        {/*
          Sikten, med tallene bak. Bare på utsiktspunkt og over 500 moh —
          serveren avgjør det, se api/src/services/clarity.ts.
        */}
        {stop.clarity && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.clarityLabel}</h3>
            <ClarityBadge clarity={stop.clarity} lang={lang} />
          </div>
        )}

        {/*
          Hva adkomsten krever av et stort kjøretøy. Vises uansett hva brukeren
          har valgt: den som åpner detaljpanelet har spurt om stedet, og da er
          en enfeltsvei med møteplasser en opplysning og ikke en advarsel.
        */}
        {stop.vehicle && (
          <div className="drawer-section">
            <div className="vehicle-access">
              <TriangleAlert size={14} />
              <div>
                <p>{stop.vehicle.note}</p>
                <div className="vehicle-flags">
                  {stop.vehicle.narrowRoad && <span>{t.vehicleNarrow}</span>}
                  {stop.vehicle.steepIncline && <span>{t.vehicleSteep}</span>}
                  {stop.vehicle.maxVehicleLengthMeters !== undefined && (
                    <span>{t.vehicleMaxLength(stop.vehicle.maxVehicleLengthMeters)}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/*
          `?.` paa samlingene, ikke fordi typen sier de kan mangle — den sier
          det motsatte — men fordi tradraaden kan. `endpoints` sendte raa stopp
          uten `producers`, `heritage`, `bathingSpots` og `amenities`, og
          `undefined.length` her tok ned hele appen. Kilden er rettet (se
          `asEndpoint` paa serveren); dette er beltet ved siden av selen.
        */}
        {(stop.producers?.length ?? 0) > 0 && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.producerTitle}</h3>
            <ProducerPanel producers={stop.producers} lang={lang} near={stop.name} />
          </div>
        )}

        {/*
          Sjø og bading. Vises bare når det finnes noe: temperaturen kommer bare
          i badesesongen og bare der havmodellen har dekning, og badeplassene er
          tomme for de fleste innlandsstoppene. Se services/ocean.ts.
        */}
        {(stop.sea || (stop.bathingSpots?.length ?? 0) > 0) && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.seaTitle}</h3>
            <SeaPanel sea={stop.sea} spots={stop.bathingSpots} lang={lang} />
          </div>
        )}

        {stop.cameras && <CameraStrip cameras={stop.cameras} lang={lang} />}

        {stop.winterWarning && (
          <div className="banner banner--warn" style={{ marginTop: 16 }}>
            <AlertTriangle size={15} />
            <span>{stop.winterWarning}</span>
          </div>
        )}

        {/*
          Vinteren som innhold, rett under vinteradvarselen.
          
          Rekkefølgen er valgt: advarselen sier hva som ikke går, panelet sier
          hva som går i stedet. Sto panelet lenger ned, ville en desemberbruker
          lest fire advarsler og lukket panelet før de kom til nordlyset,
          hundekjøringen og skreien — som er grunnen folk faktisk reiser hit i
          desember. Se api/src/data/winter.ts.
        */}
        {stop.winter && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.winterTitle}</h3>
            <WinterPanel winter={stop.winter} lang={lang} />
          </div>
        )}

        <ul className="drawer-facts">
          <li>
            <dt>{t.elevation}</dt>
            <b>
              {stop.elevationM} {t.unitMasl}
            </b>
          </li>
          <li>
            <dt>{t.travelFromStart}</dt>
            <b>~{formatHours(stop.hoursFromStart, lang)}</b>
          </li>
          <li>
            <dt>{t.suggestedTime}</dt>
            <b>~{formatHours(stop.dwellHours, lang)}</b>
          </li>
          <li>
            <dt>{t.viewDependent}</dt>
            <b>{Math.round(stop.viewDependency * 100)} %</b>
          </li>
          <li>
            <dt>{t.reachedBy}</dt>
            {/* Er stedet bare nåbart til fots, er det startpunktet man reiser til. */}
            <b>
              {(stop.hike ? stop.hike.trailheadModes : stop.modes)
                .map((mode) => t.modeName[mode])
                .join(", ")}
              {stop.hike && ` + ${t.modeName.fottur}`}
            </b>
          </li>
          {daylight && (
            <li>
              <dt>{t.daylight}</dt>
              <b>{daylightSummary(daylight, lang)}</b>
            </li>
          )}
          <li>
            <dt>{t.coordinates}</dt>
            <b>
              {stop.lat.toFixed(4)}, {stop.lon.toFixed(4)}
            </b>
          </li>
          {stop.enturStopId && (
            <li>
              <dt>{t.enturStop}</dt>
              <b style={{ fontSize: 11 }}>{stop.enturStopId}</b>
            </li>
          )}
        </ul>

        {stop.indoorAlternative && (
          <p className="drawer-indoor">
            <b>{t.whenItRains} </b>
            {stop.indoorAlternative}
          </p>
        )}

        {/*
          ── Resten, bak ett klikk ──────────────────────────────────────────

          Meldt fra bruk: «for hvert sted er det mye informasjon — la oss skjule
          det også, og bare vise det meningsfulle og viktigste».

          Over står det man tar en beslutning på: hva stedet er, om det er
          vinterstengt, været og utsikten, fotturen og dagslyset. Under lå
          opplevelser, historie, klimanormaler, kulturminner, nordlys, bussruter,
          veikameraer, overnatting og tjenester — fjorten seksjoner, alle åpne
          samtidig, på hvert eneste sted.

          De er ikke fjernet. Et sted man vurderer å sove på, trenger
          hotellista — men ikke før man har bestemt seg for å dra dit.
        */}
        <FoldPanel
          id="stopExtras"
          title={t.dayInfoTitle}
          icon={<Info size={14} />}
          summary={t.stopExtrasSummary}
        >
        {stop.offers && stop.offers.length > 0 && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.offersTitle}</h3>
            <ul className="offer-list">
              {stop.offers.map((offer) => (
                <li className="offer" key={offer.name}>
                  <span className={`offer-kind offer-kind--${offer.kind}`}>
                    {t.offerKind[offer.kind]}
                  </span>
                  <span className="offer-body">
                    <b>{offer.name}</b>
                    {offer.note && <small>{offer.note}</small>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {stop.history && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.historyTitle}</h3>
            <p className="drawer-history">{stop.history}</p>
            {stop.historySource && (
              // CC BY-SA krever lenke tilbake til artikkelen teksten kom fra.
              <a
                className="drawer-source"
                href={stop.historySource}
                target="_blank"
                rel="noreferrer"
              >
                {t.historySource} <ExternalLink size={11} />
              </a>
            )}
          </div>
        )}

        {/*
          Nordlysvarselet står bare der det gir mening: nordlig nok sted, dato
          satt, og innenfor NOAA sine tre døgn. Ellers finnes feltet ikke.
        */}
        {stop.climate && month && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.climateTitle}</h3>
            <ClimatePanel climate={stop.climate} month={month} lang={lang} />
          </div>
        )}

        {(stop.heritage?.length ?? 0) > 0 && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.heritageTitle}</h3>
            <HeritagePanel sites={stop.heritage} lang={lang} />
          </div>
        )}

        {stop.aurora && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.auroraTitle}</h3>
            <div className={`aurora ${stop.aurora.chance >= 0.4 ? "aurora--good" : ""}`}>
              <div className="aurora-head">
                <Sparkles size={13} />
                <strong>{t.auroraChance(Math.round(stop.aurora.chance * 100))}</strong>
                <span className="aurora-kp">
                  {t.auroraKp(stop.aurora.kp, stop.aurora.requiredKp)}
                </span>
              </div>
              <p className="aurora-note">
                {stop.aurora.limitedBy ? t.auroraLimited[stop.aurora.limitedBy] : t.auroraGood}
              </p>
              <span className="aurora-source">{t.auroraSource}</span>
            </div>
          </div>
        )}

        {/*
          Hva som går herfra, før alt annet i listen.

          Rekkefølgen er den spørsmålene kommer i. For et stopp man vurderer å
          legge inn står «kommer jeg meg hit, og kommer jeg videre» før både
          natur og senger — er svaret fire avganger i døgnet, endrer det hele
          vurderingen av de andre.
        */}
        {/*
          Seksjonen sto bak `stop.enturStopId`, og det var feil vei rundt.
          
          41 stopp har ingen holdeplass av sitt eget — hele Jotunheimen-
          korridoren blant dem — og de er nettopp de stoppene spørsmålet
          «hvordan kommer jeg meg herfra» betyr mest for. Porten skjulte altså
          svaret der det trengtes. Panelet håndterer begge tilfellene selv nå:
          uten en NSR-id hopper det over avgangshalvdelen og viser bare naboene.
        */}
        <div className="drawer-section">
          <h3 className="drawer-section-title">{t.linesTitle}</h3>
          <p className="drawer-section-sub">{t.linesSubtitle}</p>
          <LinesPanel stopId={stop.id} lang={lang} />
        </div>

        {/*
          Vernet natur og allemannsretten står før overnatting: hvor man får
          sette opp teltet avgjøres av forskriften for området, ikke av hva
          OpenStreetMap kjenner av campingplasser.
        */}
        {stop.protectedAreas && stop.protectedAreas.length > 0 && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.protectedTitle}</h3>
            <ProtectedAreaPanel areas={stop.protectedAreas} lang={lang} />
          </div>
        )}

        {/* Vintertilbudet står bare der stedet faktisk har et. */}
        {stop.ski && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{t.skiTitle}</h3>
            <SkiPanel ski={stop.ski} lang={lang} near={stop.name} />
          </div>
        )}

        {/* Overnatting før fasilitetene: hvor man sover er en større
            avgjørelse enn hvor man lader, og styrer hvor dagen slutter. */}
        <div className="drawer-section">
          <h3 className="drawer-section-title">{t.lodgingTitle}</h3>
          <LodgingList
            lodging={(stop.lodging ?? []).filter((e) => !placeKinds || placeKinds.has(e.kind))}
            lang={lang}
            near={stop.name}
          />
        </div>

        <div className="drawer-section">
          <h3 className="drawer-section-title">{t.amenitiesTitle}</h3>
          <AmenityList
            amenities={(stop.amenities ?? []).filter((a) => !placeKinds || placeKinds.has(a.kind))}
            lang={lang}
            near={stop.name}
          />
        </div>

        </FoldPanel>

        <a href={mapUrl} target="_blank" rel="noreferrer" className="drawer-link">
          {t.seeOnMap} <ExternalLink size={13} />
        </a>
      </div>
    </div>
  );
}
